/**
 * BL-product-grouping.js
 * AJAX-based navigation for product grouping swatches.
 * Replaces full-page reload with a section-only fetch + DOM swap.
 *
 * Selector contract: targets [data-bl-grouping-root] exclusively.
 * This avoids collisions with .bl-product-configurator used by
 * BL-product-addon-configurator and BL-washbasin-selector-v2.
 */
(function () {
  let abortController = null;
  const sectionCache = new Map();
  const preparedSectionCache = new Map();
  const pendingPrefetches = new Map();
  let persistentAddonBlockState = null;
  let persistentWashbasinBlockState = null;
  let persistentMediaGalleryState = null;
  let persistentTolstoyBlockState = null;
  const CACHE_TTL = 5 * 60 * 1000;
  const CACHE_MAX = 20;
  const PREPARED_CACHE_MAX = 40;
  const EAGER_PREFETCH_LIMIT = 12;
  const initialHandleNormalizationKeys = new Set();
  // Tracks image keys already queued for preload so we never insert duplicate
  // <link rel="preload"> tags across multiple prefetch calls.
  const _preloadedImgKeys = new Set();

  function init() {
    const wrapper = document.querySelector('[data-bl-grouping-root]');
    if (!wrapper) return;

    loadGroupingMatrix(wrapper);
    bindPopstate();
    if (normalizeInitialHandleMatch(wrapper)) return;

    bindHoverHandlers(wrapper);
    bindClickHandler(wrapper);
    eagerPrefetch(wrapper);
  }

  function getSectionIdFromNode(node) {
    if (!node || !node.id) return null;
    return node.id.replace(/^shopify-section-/, '');
  }

  function getSectionTargets(wrapper) {
    const productInfo = document.querySelector('product-info[id^="MainProduct-"]');
    const mainSectionNode =
      wrapper?.closest('.shopify-section')
      || productInfo?.closest('.shopify-section');

    const stickySectionNode = document.getElementById('shopify-section-sticky-atc-bar');

    return {
      main: {
        node: mainSectionNode,
        id: getSectionIdFromNode(mainSectionNode)
      },
      sticky: {
        node: stickySectionNode,
        id: getSectionIdFromNode(stickySectionNode)
      }
    };
  }

  function buildSectionRequestUrl(url, sectionIds) {
    const requestUrl = new URL(url, window.location.href);
    requestUrl.searchParams.set('sections', sectionIds.join(','));
    return requestUrl.toString();
  }

  function cloneSectionShell(node) {
    if (!node) return null;

    const shell = document.createElement(node.tagName.toLowerCase());
    Array.from(node.attributes).forEach(attribute => {
      shell.setAttribute(attribute.name, attribute.value);
    });

    return shell;
  }

  function parseSectionMarkup(markup, fallbackNode = null) {
    if (!markup) return null;

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const wrappedSection = doc.querySelector('.shopify-section');
    if (wrappedSection) return wrappedSection;

    if (fallbackNode) {
      const shell = cloneSectionShell(fallbackNode);
      if (shell) {
        shell.innerHTML = markup;
        return shell;
      }
    }

    return doc.body.firstElementChild;
  }

  function getSelectedVariantFromSection(sectionNode) {
    const variantScript = sectionNode?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
    if (!variantScript) return null;

    try {
      return JSON.parse(variantScript.textContent);
    } catch (error) {
      return null;
    }
  }

  function updateDocumentTitle(sectionNode) {
    const titleNode = sectionNode?.querySelector('.product__block--title .product__title, .product__title');
    const nextTitle = titleNode?.textContent?.trim();
    if (!nextTitle) return;

    const currentTitle = document.title || '';
    const separators = [' | ', ' – ', ' - '];
    const separator = separators.find(item => currentTitle.includes(item));

    if (!separator) {
      document.title = nextTitle;
      return;
    }

    const suffix = currentTitle.split(separator).slice(1).join(separator).trim();
    document.title = suffix ? `${nextTitle}${separator}${suffix}` : nextTitle;
  }

  function applyLoadingState(node, isLoading) {
    if (!node) return;

    node.style.opacity = '';
    node.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function captureViewportPosition() {
    return {
      x: window.scrollX,
      y: window.scrollY
    };
  }

  function restoreViewportPosition(position) {
    if (!position) return;

    const restore = () => {
      if (window.scrollX !== position.x || window.scrollY !== position.y) {
        window.scrollTo(position.x, position.y);
      }
    };

    restore();
    requestAnimationFrame(restore);
  }

  function getCached(key) {
    const entry = sectionCache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.ts > CACHE_TTL) {
      sectionCache.delete(key);
      return null;
    }

    return entry.data;
  }

  function setCached(key, data) {
    if (sectionCache.size >= CACHE_MAX) {
      const oldestKey = sectionCache.keys().next().value;
      if (oldestKey) sectionCache.delete(oldestKey);
    }

    sectionCache.set(key, {
      data,
      ts: Date.now()
    });
  }

  function getPreparedCached(key) {
    const entry = preparedSectionCache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.ts > CACHE_TTL) {
      preparedSectionCache.delete(key);
      return null;
    }

    return entry.data;
  }

  function setPreparedCached(key, data) {
    if (preparedSectionCache.size >= PREPARED_CACHE_MAX) {
      const oldestKey = preparedSectionCache.keys().next().value;
      if (oldestKey) preparedSectionCache.delete(oldestKey);
    }

    preparedSectionCache.set(key, {
      data,
      ts: Date.now()
    });
  }

  function fetchSectionMap(requestUrl, options = {}) {
    const cached = getCached(requestUrl);
    if (cached) return Promise.resolve(cached);

    const pending = pendingPrefetches.get(requestUrl);
    if (pending) return pending;

    const fetchOptions = {};
    if (options.signal) fetchOptions.signal = options.signal;

    const promise = fetch(requestUrl, fetchOptions)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(sectionMap => {
        setCached(requestUrl, sectionMap);
        return sectionMap;
      })
      .finally(() => {
        if (pendingPrefetches.get(requestUrl) === promise) {
          pendingPrefetches.delete(requestUrl);
        }
      });

    pendingPrefetches.set(requestUrl, promise);
    return promise;
  }

  function loadGroupingMatrix(wrapper) {
    const sectionNode = getSectionTargets(wrapper).main.node;
    const matrixScript = sectionNode?.querySelector('[id^="bl-grouping-matrix-"]') || document.querySelector('[id^="bl-grouping-matrix-"]');

    try {
      window.__blGroupingMatrix = JSON.parse(matrixScript?.textContent || '{}');
    } catch (error) {
      window.__blGroupingMatrix = {};
    }
  }

  function normalizeOptionText(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function normalizeSizeKey(value) {
    return normalizeOptionText(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function extractNumericSizeToken(value) {
    return (value || '').toString().replace(/[^\d]/g, '');
  }

  function getComparableSizeValue(value) {
    return normalizeOptionText(value)
      .replace(/\s+/g, '')
      .replace(/[.,/x×-]/g, '')
      .replace(/"/g, '')
      .replace(/'/g, '')
      .replace(/inch|in|mm|cm|2s/g, '');
  }

  function looksLikeSizeValue(value) {
    const normalized = normalizeOptionText(value);
    const comparable = getComparableSizeValue(value);
    const numeric = extractNumericSizeToken(value);
    if (!normalized) return false;

    if (/^\d+([.,]\d+)?$/.test(normalized)) return true;
    if (/^\d+([.,]\d+)?\s*2s$/.test(normalized)) return true;
    if (/\d\s*(cm|mm)\b/.test(normalized)) return true;
    if (/^\d+([.,]\d+)?\s*(x|×|-|\/)\s*\d+([.,]\d+)?/.test(normalized)) return true;

    return comparable.length >= 2 && numeric.length >= 2 && !/[a-z]/.test(comparable);
  }

  function getSelectedValueFromOptionBlock(block) {
    if (!block) return '';

    const selectedSpan = block.querySelector('[data-selected-value]');
    if (selectedSpan?.textContent?.trim()) return selectedSpan.textContent.trim();

    const checked = block.querySelector('input[type="radio"]:checked');
    if (checked?.value?.trim()) return checked.value.trim();

    const select = block.querySelector('select');
    if (select?.value?.trim()) return select.value.trim();

    const active = block.querySelector('.is-active, .selected, [aria-checked="true"]');
    const activeValue = active?.dataset?.value || active?.dataset?.optionValue || active?.textContent;
    return activeValue?.trim() || '';
  }

  function getOptionValuesFromBlock(block) {
    if (!block) return [];

    const values = new Set();

    block.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (input.value?.trim()) values.add(input.value.trim());
    });

    block.querySelectorAll('select option').forEach((option) => {
      if (option.value?.trim()) values.add(option.value.trim());
      const optionText = option.textContent?.trim();
      if (optionText) values.add(optionText);
    });

    block.querySelectorAll('[data-value], [data-option-value]').forEach((node) => {
      const optionValue = node.dataset?.value || node.dataset?.optionValue || '';
      if (optionValue.trim()) values.add(optionValue.trim());
    });

    const selectedValue = getSelectedValueFromOptionBlock(block);
    if (selectedValue) values.add(selectedValue);

    return Array.from(values);
  }

  function blockLooksLikeSizeOption(block) {
    const values = getOptionValuesFromBlock(block).filter(Boolean);
    if (!values.length) return false;

    const sizeLikeValues = values.filter(looksLikeSizeValue);
    if (sizeLikeValues.length > 1) return true;
    if (sizeLikeValues.length > 0 && sizeLikeValues.length === values.length) return true;

    return looksLikeSizeValue(getSelectedValueFromOptionBlock(block));
  }

  function getStoredSelectedLabel(group, fallback = '') {
    if (!group) return fallback;

    const stored = group.dataset.blSelectedLabel;
    if (stored && stored.trim()) return stored.trim();

    const label = group.querySelector('[data-selected-value]')?.textContent?.trim();
    if (label) {
      group.dataset.blSelectedLabel = label;
      return label;
    }

    return fallback;
  }

  function getCurrentSelectedSize() {
    const picker = document.querySelector('variant-selects, variant-radios');

    if (picker) {
      const allBlocks = picker.querySelectorAll('fieldset, .product-form__input, .variant-picker__option');
      for (const block of allBlocks) {
        if (!blockLooksLikeSizeOption(block)) continue;

        const selectedValue = getSelectedValueFromOptionBlock(block);
        if (selectedValue) return selectedValue;
      }
    }

    const productSection = getSectionTargets(document.querySelector('[data-bl-grouping-root]')).main.node;
    const selectedVariant = getSelectedVariantFromSection(productSection);
    if (selectedVariant?.options?.length) {
      const candidate = selectedVariant.options.find(looksLikeSizeValue);
      if (candidate) return candidate;
    }

    return null;
  }

  function resolveTargetVariantId(url) {
    const matrix = window.__blGroupingMatrix || {};
    const targetPath = new URL(url, window.location.href).pathname;
    const siblingMap = matrix[targetPath];
    const currentSize = getCurrentSelectedSize();

    if (!siblingMap || !currentSize) return null;

    const exactKey = normalizeSizeKey(currentSize);
    if (siblingMap[exactKey]) return siblingMap[exactKey];

    const numericSize = extractNumericSizeToken(currentSize);
    if (!numericSize) return null;

    const matchedEntry = Object.entries(siblingMap).find(([sizeKey]) => {
      return extractNumericSizeToken(sizeKey) === numericSize;
    });

    return matchedEntry ? matchedEntry[1] : null;
  }

  function buildTargetProductUrl(url) {
    const targetUrl = new URL(url, window.location.href);
    const variantId = resolveTargetVariantId(targetUrl.toString());

    if (variantId) {
      targetUrl.searchParams.set('variant', variantId);
    } else {
      targetUrl.searchParams.delete('variant');
    }

    return targetUrl.toString();
  }

  function getCurrentWashbasinSelection() {
    const checkedInput = document.querySelector('[data-washbasin-selector-v2] .washbasin-selector__input:checked');
    if (checkedInput?.value) return checkedInput.value;

    const activeItem = document.querySelector('[data-washbasin-selector-v2] .washbasin-selector__item.is-active');
    if (!activeItem) return null;

    if (activeItem.hasAttribute('data-washbasin-none')) return 'none';
    return activeItem.dataset.washbasinId || null;
  }

  function getCurrentAddonCustomizer() {
    return document.querySelector('bl-product-customizer');
  }

  function getCurrentAddonBlock() {
    return document.querySelector('.product__block--BL_addon_config');
  }

  function captureCurrentAddonSwapState() {
    const customizer = getCurrentAddonCustomizer();
    if (!customizer || typeof customizer.exportSwapState !== 'function') {
      window.__blAddonConfiguratorSwapState = null;
      return null;
    }

    const state = customizer.exportSwapState();
    window.__blAddonConfiguratorSwapState = state;
    return state;
  }

  function getCurrentAddonSwapState() {
    const customizer = getCurrentAddonCustomizer();
    if (customizer && typeof customizer.exportSwapState === 'function') {
      return customizer.exportSwapState();
    }

    return window.__blAddonConfiguratorSwapState || null;
  }

  function capturePersistentAddonBlockState() {
    const blockNode = getCurrentAddonBlock();
    const customizer = blockNode?.querySelector('bl-product-customizer') || getCurrentAddonCustomizer();

    if (!blockNode || !customizer) {
      persistentAddonBlockState = null;
      return null;
    }

    persistentAddonBlockState = {
      blockNode,
      customizer
    };

    return persistentAddonBlockState;
  }

  function syncBlockAttributes(targetBlock, sourceBlock) {
    if (!targetBlock || !sourceBlock) return;

    Array.from(targetBlock.attributes).forEach((attribute) => {
      sourceBlock.removeAttribute(attribute.name);
    });

    Array.from(sourceBlock.attributes).forEach((attribute) => {
      targetBlock.removeAttribute(attribute.name);
    });

    Array.from(targetBlock.attributes).forEach((attribute) => {
      sourceBlock.setAttribute(attribute.name, attribute.value);
    });
  }

  function restorePersistentAddonBlock(newNode) {
    const preservedBlock = persistentAddonBlockState?.blockNode;
    const preservedCustomizer = persistentAddonBlockState?.customizer;
    const incomingBlock = newNode?.querySelector('.product__block--BL_addon_config');
    if (!preservedBlock || !preservedCustomizer || !incomingBlock) return;

    syncBlockAttributes(incomingBlock, preservedBlock);
    incomingBlock.replaceWith(preservedBlock);

    if (typeof preservedCustomizer.updateProductContextFromNode === 'function') {
      preservedCustomizer.updateProductContextFromNode(incomingBlock);
    }
  }

  function getMediaGallerySignature(rootNode) {
    const gallery = rootNode?.querySelector('media-gallery');
    if (!gallery) return '';

    return Array.from(gallery.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)'))
      .map((item) => item.dataset.mediaId || '')
      .filter(Boolean)
      .join(',');
  }

  function capturePersistentMediaGalleryState() {
    const galleryNode = document.querySelector('media-gallery');
    const instance = galleryNode || null;

    if (!galleryNode) {
      persistentMediaGalleryState = null;
      return null;
    }

    persistentMediaGalleryState = {
      galleryNode,
      instance,
      signature: getMediaGallerySignature(document)
    };

    return persistentMediaGalleryState;
  }

  function restorePersistentMediaGallery(newNode) {
    const preserved = persistentMediaGalleryState;
    const incomingGallery = newNode?.querySelector('media-gallery');
    if (!preserved?.galleryNode || !incomingGallery) return false;

    const incomingIsServerFiltered = incomingGallery.matches?.('media-gallery[data-bl-server-gallery-filter="true"]');
    const preservedIsServerFiltered = preserved.galleryNode.matches?.('media-gallery[data-bl-server-gallery-filter="true"]');
    if (incomingIsServerFiltered || preservedIsServerFiltered) {
      return false;
    }

    const currentSignature = preserved.signature;
    const incomingSignature = getMediaGallerySignature(newNode);

    if (!currentSignature || !incomingSignature || currentSignature !== incomingSignature) {
      return false;
    }

    syncBlockAttributes(incomingGallery, preserved.galleryNode);
    incomingGallery.replaceWith(preserved.galleryNode);
    return true;
  }

  function capturePersistentWashbasinBlockState() {
    const blockNode = document.querySelector('.product__block--BL_washbasin_selector_v2');
    const container = blockNode?.querySelector('[data-washbasin-selector-v2]');
    const slider = container?.querySelector('[data-washbasin-slider]');

    if (!blockNode || !container) {
      persistentWashbasinBlockState = null;
      return null;
    }

    persistentWashbasinBlockState = {
      blockNode,
      container,
      instance: container.__blWbInstance || null,
      selectedValue: container.__blWbInstance?.getSelectedValue?.() || null,
      scrollLeft: slider ? slider.scrollLeft : 0
    };

    return persistentWashbasinBlockState;
  }

  function getCurrentTolstoyBlock() {
    return document.querySelector('tolstoy-stories')?.closest('.product__block') || null;
  }

  function capturePersistentTolstoyBlockState() {
    const blockNode = getCurrentTolstoyBlock();
    const tolstoyNode = blockNode?.querySelector('tolstoy-stories') || null;

    if (!blockNode || !tolstoyNode) {
      persistentTolstoyBlockState = null;
      return null;
    }

    persistentTolstoyBlockState = {
      blockNode,
      tolstoyNode
    };

    return persistentTolstoyBlockState;
  }

  function restorePersistentTolstoyBlock(newNode) {
    const preserved = persistentTolstoyBlockState;
    const incomingBlock = newNode?.querySelector('tolstoy-stories')?.closest('.product__block');
    if (!preserved?.blockNode || !preserved?.tolstoyNode || !incomingBlock) return;

    syncBlockAttributes(incomingBlock, preserved.blockNode);
    incomingBlock.replaceWith(preserved.blockNode);
  }

  function restorePersistentWashbasinBlock(newNode) {
    const preserved = persistentWashbasinBlockState;
    if (!preserved?.blockNode) return;

    const incomingBlock = newNode?.querySelector('.product__block--BL_washbasin_selector_v2');
    if (!incomingBlock) return;

    // Extract the NEW product's config from the incoming section before we discard it
    const incomingContainer = incomingBlock.querySelector('[data-washbasin-selector-v2]');
    const incomingConfigEl = incomingContainer?.querySelector('[data-washbasin-config]');
    let incomingConfig = null;
    if (incomingConfigEl) {
      try { incomingConfig = JSON.parse(incomingConfigEl.textContent); } catch (e) {}
    }

    // Replace the incoming block with our preserved (live) block
    syncBlockAttributes(incomingBlock, preserved.blockNode);
    incomingBlock.replaceWith(preserved.blockNode);

    // Update the surviving instance's config with the NEW product's variant data
    if (preserved.instance && incomingConfig) {
      if (incomingConfig.variantPrices) preserved.instance.config.variantPrices = incomingConfig.variantPrices;
      if (incomingConfig.variantWidths) preserved.instance.config.variantWidths = incomingConfig.variantWidths;
      if (incomingConfig.productId) preserved.instance.config.productId = incomingConfig.productId;
      if (incomingConfig.sectionId) preserved.instance.config.sectionId = incomingConfig.sectionId;
      if (incomingConfig.currentWidth) preserved.instance.config.currentWidth = incomingConfig.currentWidth;

      // Also update the JSON script inside the preserved block so future reads are correct
      const preservedConfigEl = preserved.container.querySelector('[data-washbasin-config]');
      if (preservedConfigEl && incomingConfigEl) {
        preservedConfigEl.textContent = incomingConfigEl.textContent;
      }
    }

    // Re-bind to the new variant picker and update width — silently to avoid visual jumps
    if (preserved.instance) {
      const selectedValue = preserved.selectedValue || preserved.instance.getSelectedValue?.() || 'none';
      const preservedScrollLeft = typeof preserved.scrollLeft === 'number' ? preserved.scrollLeft : 0;

      // Suspend auto-centering briefly so post-swap sync handlers can update
      // visibility/price without recentering the slider. User clicks should
      // still be able to center immediately after the swap.
      preserved.instance._suspendSyncUntil = Date.now() + 900;
      preserved.instance._skipGroupingSwapped = true;

      preserved.instance.refreshVariantBindings();
      preserved.instance.currentMainVariantId = preserved.instance.getCurrentMainVariantId();
      // Use silent width update: detect new width and update state without scroll animation
      const newWidth = preserved.instance.detectWidthFromDOM()
        || preserved.instance.extractWidthFromVariant(preserved.instance.getSelectedMainVariant());
      if (newWidth) {
        preserved.instance.currentWidth = newWidth;
        // Update item visibility for the new width without re-selecting (no scroll jump)
        preserved.instance.items.forEach(item => {
          if (item.hasAttribute('data-washbasin-none')) return;
          const match = preserved.instance.getMatchForWidth(item, newWidth);
          item.style.display = match.variantId ? '' : 'none';
          const pill = preserved.instance.container.querySelector(`[data-pill-value="${item.dataset.washbasinId}"]`);
          if (pill) pill.style.display = match.variantId ? '' : 'none';
          if (match.variantId) {
            const pDisp = item.querySelector('[data-washbasin-price-display]');
            if (pDisp) { pDisp.textContent = `+${preserved.instance.formatMoney(match.price)}`; pDisp.style.display = ''; }
          }
        });
      }

      // Restore the previous explicit basin choice when still valid.
      if (selectedValue) {
        preserved.instance.ensureValidSelection(selectedValue, true);
      }

      // Preserve the exact horizontal viewport instead of recentering.
      // Temporarily suspend CSS scroll-snap-type so the direct scrollLeft
      // assignment is not corrected by the browser to the nearest snap point
      // (snap points may shift when items are shown/hidden for the new width).
      const restoreSliderPosition = () => {
        const sl = preserved.instance.slider;
        if (!sl) return;
        // Disable snap, set position, re-enable snap in the NEXT rAF so the
        // position is committed to layout before snap re-engages.  On that
        // re-engagement the browser will find the set position already IS a
        // valid snap point (we chose it from a previously-snapped state) and
        // make no further adjustment.
        sl.style.scrollSnapType = 'none';
        sl.scrollLeft = preservedScrollLeft;
        requestAnimationFrame(() => {
          if (sl.style.scrollSnapType === 'none') sl.style.scrollSnapType = '';
        });
        preserved.instance.updateProgressBar?.();
      };

      requestAnimationFrame(() => {
        restoreSliderPosition();
        requestAnimationFrame(restoreSliderPosition);
      });

      // Update price without triggering scroll
      preserved.instance.scheduleGlobalPriceUpdate();
    }
  }

  function getAddonPreparationSignature(swapState = getCurrentAddonSwapState()) {
    if (!swapState || swapState.mode === 'none') return '';

    return JSON.stringify({
      mode: swapState.mode,
      basin: swapState.basin || {},
      top: swapState.top || {},
      size: getCurrentSelectedSize() || ''
    });
  }

  function getPreparedSectionCacheKey(requestUrl, signature) {
    if (!requestUrl || !signature) return '';
    return `${requestUrl}::${signature}`;
  }

  function getCurrentWashbasinContainer() {
    return document.querySelector('[data-washbasin-selector-v2]');
  }

  function getCurrentWashbasinConfig(container = getCurrentWashbasinContainer()) {
    const configNode = container?.querySelector('[data-washbasin-config]');
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function getSelectedWashbasinItem(container = getCurrentWashbasinContainer()) {
    return container?.querySelector('.washbasin-selector__item.is-active:not([data-washbasin-none])')
      || null;
  }

  function getVariantSizeFromVariant(variant) {
    if (!variant?.options?.length) return null;
    return variant.options.find(looksLikeSizeValue) || null;
  }

  function getWashbasinMatchForWidth(item, targetWidth) {
    if (!item || !targetWidth) return { variantId: null, price: 0 };

    let mapping = {};
    let prices = {};

    try {
      mapping = JSON.parse(item.dataset.mapping || '{}');
      prices = JSON.parse(item.dataset.prices || '{}');
    } catch (error) {
      return { variantId: null, price: 0 };
    }

    let variantId = mapping[targetWidth];
    let price = prices[targetWidth];

    if (!variantId) {
      const cleanTarget = targetWidth.toLowerCase().replace(/[^a-z0-9]/g, '');
      const targetNum = extractNumericSizeToken(targetWidth);

      for (const key of Object.keys(mapping)) {
        const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        const keyNum = extractNumericSizeToken(key);
        if (cleanKey === cleanTarget || (targetNum && keyNum === targetNum)) {
          variantId = mapping[key];
          price = prices[key];
          break;
        }
      }
    }

    return {
      variantId: variantId || null,
      price: Number.parseInt(price || 0, 10) || 0
    };
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      const format =
        window.FoxTheme?.settings?.moneyFormat
        || document.querySelector('[data-money-format]')?.getAttribute('data-money-format')
        || document.querySelector('[data-money-format]')?.content
        || '${{amount}}';
      return window.Shopify.formatMoney(cents, format);
    }

    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  }

  function buildBundlePriceState(basePrice, addonPrice, {
    baseCompareAtPrice = 0,
    prefix = '',
    owner = 'washbasin-v2',
    allowCompareAt = true
  } = {}) {
    const total = basePrice + addonPrice;
    const displayPrice = formatMoney(total);
    let compareAtTotal = 0;

    if (allowCompareAt && baseCompareAtPrice > basePrice && basePrice > 0 && addonPrice > 0) {
      compareAtTotal = Math.round((total * baseCompareAtPrice) / basePrice);
    }

    return {
      total,
      formattedTotal: `${prefix}${displayPrice}`,
      displayPrice: `${prefix}${displayPrice}`,
      compareAtTotal,
      compareAtPrice: compareAtTotal > total ? formatMoney(compareAtTotal) : '',
      isOnSale: compareAtTotal > total,
      owner
    };
  }

  function normalizeComparableValue(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function isSameSizeValue(a, b) {
    const normalizedA = normalizeComparableValue(a);
    const normalizedB = normalizeComparableValue(b);

    if (!normalizedA || !normalizedB) return false;
    if (normalizedA === normalizedB) return true;

    const numericA = extractNumericSizeToken(a);
    const numericB = extractNumericSizeToken(b);
    return numericA.length >= 2 && numericA === numericB;
  }

  function addonVariantMatchesSize(variant, targetWidth) {
    if (!targetWidth) return true;

    return [variant?.option1, variant?.option2, variant?.option3].some((optionValue) =>
      isSameSizeValue(optionValue, targetWidth)
    );
  }

  function parseSectionJson(sectionNode, selector, fallbackValue) {
    const script = sectionNode?.querySelector(selector);
    if (!script) return fallbackValue;

    try {
      return JSON.parse(script.textContent || JSON.stringify(fallbackValue));
    } catch (error) {
      return fallbackValue;
    }
  }

  function getIncomingAddonData(nextMainSection) {
    const customizerNode = nextMainSection?.querySelector('bl-product-customizer');
    const productId = customizerNode?.dataset.productId;
    if (!productId) return null;

    return {
      basinData: parseSectionJson(nextMainSection, `#BL-Basin-Collections-Map-${productId}`, []),
      topData: parseSectionJson(nextMainSection, `#BL-Top-Products-Map-${productId}`, []),
      productId
    };
  }

  function basinRecordMatchesSelection(record, basinState = {}) {
    if (!record) return false;
    if (basinState.color && record.color_handle !== basinState.color) return false;
    if (basinState.thickness && record.thickness_handle !== basinState.thickness) return false;
    if (basinState.design && record.design_handle !== basinState.design) return false;
    if (basinState.config && record.config_handle !== basinState.config) return false;
    if (basinState.faucet && record.faucet_handle !== basinState.faucet) return false;
    return true;
  }

  function getBasinCandidates(basinData, basinState, targetWidth, fallbackRecord = null) {
    const source = Array.isArray(basinData) && basinData.length
      ? basinData
      : (fallbackRecord ? [fallbackRecord] : []);

    return source.filter((record) =>
      basinRecordMatchesSelection(record, basinState)
      && Array.isArray(record.variants)
      && record.variants.some((variant) => addonVariantMatchesSize(variant, targetWidth))
    );
  }

  function buildBasinSelectionSubset(basinState, keys = []) {
    return keys.reduce((acc, key) => {
      if (basinState?.[key]) {
        acc[key] = basinState[key];
      }
      return acc;
    }, {});
  }

  function getFirstAvailableBasinColor(basinData, targetWidth) {
    const seenColors = new Set();

    for (const record of basinData || []) {
      const colorHandle = record?.color_handle;
      if (!colorHandle || seenColors.has(colorHandle)) continue;
      seenColors.add(colorHandle);

      if ((record.variants || []).some((variant) => addonVariantMatchesSize(variant, targetWidth))) {
        return colorHandle;
      }
    }

    return null;
  }

  function findExactAddonVariant(candidates, basinState, targetWidth) {
    if (!basinState?.native) return null;

    for (const record of candidates) {
      const matchedVariant = record.variants?.find((variant) =>
        String(variant.id) === String(basinState.native)
        && addonVariantMatchesSize(variant, targetWidth)
      );

      if (matchedVariant) return matchedVariant;
    }

    return null;
  }

  function getMinAddonVariantPrice(candidates, targetWidth) {
    let minPrice = Infinity;

    candidates.forEach((record) => {
      (record.variants || []).forEach((variant) => {
        if (!addonVariantMatchesSize(variant, targetWidth)) return;

        const variantPrice = Number.parseInt(variant.price || 0, 10);
        if (variantPrice > 0 && variantPrice < minPrice) {
          minPrice = variantPrice;
        }
      });
    });

    return minPrice === Infinity ? 0 : minPrice;
  }

  function buildIncomingBasinBundleState(nextMainSection, swapState, nextVariant, targetWidth) {
    const basinState = swapState?.basin || {};
    const incomingAddonData = getIncomingAddonData(nextMainSection);
    const incomingBasinData = incomingAddonData?.basinData || [];
    const selectionLevels = [
      ['color', 'thickness', 'design', 'config', 'faucet'],
      ['color', 'thickness', 'design', 'config'],
      ['color', 'thickness', 'design'],
      ['color', 'thickness'],
      ['color']
    ];

    let basinCandidates = [];

    for (const keys of selectionLevels) {
      const selectionSubset = buildBasinSelectionSubset(basinState, keys);
      if (!Object.keys(selectionSubset).length) continue;

      basinCandidates = getBasinCandidates(
        incomingBasinData,
        selectionSubset,
        targetWidth,
        swapState?.selectedBasinRecord || null
      );

      if (basinCandidates.length) break;
    }

    if (!basinCandidates.length) {
      const fallbackColor = getFirstAvailableBasinColor(incomingBasinData, targetWidth);
      if (fallbackColor) {
        basinCandidates = getBasinCandidates(
          incomingBasinData,
          { color: fallbackColor },
          targetWidth,
          swapState?.selectedBasinRecord || null
        );
      }
    }

    if (!basinCandidates.length) return null;

    const basePrice = Number.parseInt(nextVariant.price || 0, 10);
    if (!basePrice) return null;

    const exactVariant = findExactAddonVariant(basinCandidates, basinState, targetWidth);
    const addonPrice = exactVariant
      ? Number.parseInt(exactVariant.price || 0, 10)
      : getMinAddonVariantPrice(basinCandidates, targetWidth);

    if (!addonPrice) return null;

    return buildBundlePriceState(basePrice, addonPrice, {
      baseCompareAtPrice: Number.parseInt(nextVariant.compare_at_price || 0, 10),
      prefix: exactVariant ? '' : 'ab ',
      owner: 'addon-configurator',
      allowCompareAt: Boolean(exactVariant)
    });
  }

  function buildIncomingTopBundleState(nextMainSection, swapState, nextVariant, targetWidth) {
    const topState = swapState?.top || {};

    const incomingAddonData = getIncomingAddonData(nextMainSection);
    const topProducts = incomingAddonData?.topData || [];
    const topProduct = topProducts.find((item) => String(item.id) === String(topState.product))
      || topProducts.find((item) => (item.variants || []).some((variant) => addonVariantMatchesSize(variant, targetWidth)))
      || topProducts[0];
    if (!topProduct || !Array.isArray(topProduct.variants) || !topProduct.variants.length) return null;

    const matchedVariant = topProduct.variants.find((variant) => addonVariantMatchesSize(variant, targetWidth))
      || topProduct.variants.find((variant) =>
        String(variant.id) === String(topState.variantId)
      )
      || topProduct.variants[0];

    const addonPrice = Number.parseInt(matchedVariant?.price || 0, 10);
    const basePrice = Number.parseInt(nextVariant.price || 0, 10);
    if (!addonPrice || !basePrice) return null;

    return buildBundlePriceState(basePrice, addonPrice, {
      baseCompareAtPrice: Number.parseInt(nextVariant.compare_at_price || 0, 10),
      owner: 'addon-configurator'
    });
  }

  function ensurePriceLockCSS() {
    window.BLBundlePriceState?.ensureCSS?.();
  }

  function injectBundlePriceIntoNode(sectionNode, sectionId, bundlePriceState) {
    if (!sectionNode || !bundlePriceState) return;

    ensurePriceLockCSS();

    const priceBlock = sectionNode.querySelector(`#price-${sectionId}`) || sectionNode.querySelector('[id^="price-"]');
    if (!priceBlock) return;

    window.BLBundlePriceState?.applyPriceNode?.(priceBlock, bundlePriceState, { lock: true });
  }

  function buildIncomingBundleState(nextMainSection) {
    const container = getCurrentWashbasinContainer();
    const selectedBasinItem = getSelectedWashbasinItem(container);
    if (!container || !selectedBasinItem) return null;

    const nextVariant = getSelectedVariantFromSection(nextMainSection);
    if (!nextVariant?.id) return null;

    const config = getCurrentWashbasinConfig(container);

    const targetWidth =
      getVariantSizeFromVariant(nextVariant)
      || config?.variantWidths?.[String(nextVariant.id)]
      || getCurrentSelectedSize();

    if (!targetWidth) return null;

    const basinMatch = getWashbasinMatchForWidth(selectedBasinItem, targetWidth);
    if (!basinMatch.variantId || !basinMatch.price) return null;

    const basePrice = Number.parseInt(nextVariant.price || 0, 10);
    if (!basePrice) return null;

    return buildBundlePriceState(basePrice, basinMatch.price, {
      baseCompareAtPrice: Number.parseInt(nextVariant.compare_at_price || 0, 10),
      owner: 'washbasin-v2'
    });
  }

  function buildIncomingAddonBundleState(nextMainSection, swapStateOverride = null) {
    const swapState = swapStateOverride || window.__blAddonConfiguratorSwapState;
    if (!swapState || swapState.mode === 'none') return null;

    const nextVariant = getSelectedVariantFromSection(nextMainSection);
    if (!nextVariant?.id) return null;

    const targetWidth = getVariantSizeFromVariant(nextVariant) || getCurrentSelectedSize();
    if (swapState.mode === 'basin') {
      return buildIncomingBasinBundleState(nextMainSection, swapState, nextVariant, targetWidth);
    }

    if (swapState.mode === 'top') {
      return buildIncomingTopBundleState(nextMainSection, swapState, nextVariant, targetWidth);
    }

    return null;
  }

  function buildPreparedAddonSectionMap(sectionMap, wrapper, requestUrl, swapState) {
    const signature = getAddonPreparationSignature(swapState);
    const preparedCacheKey = getPreparedSectionCacheKey(requestUrl, signature);
    if (!signature || !preparedCacheKey) return null;

    const cachedPrepared = getPreparedCached(preparedCacheKey);
    if (cachedPrepared) return cachedPrepared;

    const targets = getSectionTargets(wrapper);
    const nextMainSection = parseSectionMarkup(sectionMap?.[targets.main.id], targets.main.node);
    if (!nextMainSection) return null;

    const incomingBundleState = buildIncomingAddonBundleState(nextMainSection, swapState);
    if (!incomingBundleState?.formattedTotal) return null;

    const preparedSectionMap = Object.assign({}, sectionMap);

    injectBundlePriceIntoNode(nextMainSection, targets.main.id, incomingBundleState);
    preparedSectionMap[targets.main.id] = nextMainSection.outerHTML;

    if (
      targets.sticky.node
      && targets.sticky.id
      && targets.sticky.node !== targets.main.node
      && sectionMap?.[targets.sticky.id]
    ) {
      const nextStickySection = parseSectionMarkup(sectionMap[targets.sticky.id], targets.sticky.node);
      if (nextStickySection) {
        injectBundlePriceIntoNode(nextStickySection, targets.sticky.id, incomingBundleState);
        preparedSectionMap[targets.sticky.id] = nextStickySection.outerHTML;
      }
    }

    setPreparedCached(preparedCacheKey, preparedSectionMap);
    return preparedSectionMap;
  }

  function appendWashbasinSelection(url) {
    const targetUrl = new URL(url, window.location.href);
    const basinSelection = getCurrentWashbasinSelection();

    if (basinSelection) {
      targetUrl.searchParams.set('bl_wb', basinSelection);
    } else {
      targetUrl.searchParams.delete('bl_wb');
    }

    return targetUrl.toString();
  }

  function hasVariantImageWizardApp() {
    const selectors = [
      '.rio-media-gallery',
      '[class*="rio-media-gallery"]',
      'script[src*="variant-image-wizard"]',
      'script[src*="variant_image_wizard"]',
      'script[src*="wizard-swatch"]',
      'script[src*="variant-image-wizard-swatch"]',
      '[class*="variant-image-wizard"]',
      '[data-app*="variant-image-wizard"]'
    ];

    if (selectors.some(selector => document.querySelector(selector))) {
      return true;
    }

    return Boolean(
      window.RioVizImgWizard
      || window.VariantImageWizard
      || window.variantImageWizard
      || window.rioMediaGallery
    );
  }

  function shouldBypassAjaxNavigation() {
    // Rio / Variant Image Wizard app has been deactivated; AJAX navigation is
    // always used.  hasVariantImageWizardApp() is intentionally not called.
    return false;
  }

  function applyOptimisticUpdate(btn, nextUrl) {
    const group = btn.closest('.product-form__product-group');
    if (group) {
      group.querySelectorAll('a[data-value]').forEach(el => {
        el.classList.remove('active', 'is-selected');
      });
      btn.classList.add('active', 'is-selected');

      const label = group.querySelector('[data-selected-value]');
      const nextLabel = btn.getAttribute('data-color-name') || btn.getAttribute('data-label') || '';
      if (label) {
        label.textContent = nextLabel;
      }
      group.dataset.blSelectedLabel = nextLabel || getStoredSelectedLabel(group);
    }

    history.pushState({}, '', nextUrl);
  }

  /**
   * Parse the main-section HTML from a prefetched sectionMap and eagerly insert
   * <link rel="preload" as="image"> tags for the first few gallery images.
   *
   * WHY: AJAX swaps deliver section HTML instantly (eagerPrefetch) but the
   * images still need to download.  On the first swap to any variant the
   * browser has to fetch the image from scratch, leaving the gallery blank
   * until the download finishes — that blank → image transition is the
   * visible "flash".  Preloading kicks off the image downloads during the
   * eagerPrefetch / hover phase so they are already in cache by the time the
   * user actually clicks a swatch.
   *
   * Only the first 3 images in the main GalleryViewer are preloaded to avoid
   * excessive bandwidth (thumbnails are excluded because they live outside
   * [id^="GalleryViewer"]).
   */
  function preloadSectionGalleryImages(sectionHtml) {
    if (!sectionHtml || typeof DOMParser === 'undefined') return;
    try {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      // Prefer the GalleryViewer (main slider); fall back to the full
      // media-gallery element so the function works even if the ID changes.
      const viewer = doc.querySelector('[id^="GalleryViewer"]') || doc.querySelector('media-gallery');
      if (!viewer) return;

      Array.from(viewer.querySelectorAll('img')).slice(0, 3).forEach(img => {
        const srcset = img.getAttribute('srcset') || '';
        const src    = img.getAttribute('src')    || '';
        if (!srcset && !src) return;
        // Skip data-URIs (inline SVG placeholders, spinner GIFs, etc.)
        if (src.startsWith('data:') || (srcset && srcset.startsWith('data:'))) return;

        // Derive a stable dedup key from the first URL in srcset (or src).
        const key = srcset ? srcset.split(',')[0].trim().split(' ')[0] : src;
        if (!key || _preloadedImgKeys.has(key)) return;
        _preloadedImgKeys.add(key);

        const link = document.createElement('link');
        link.rel = 'preload';
        link.as  = 'image';
        if (srcset) {
          // imagesrcset/imagesizes let the browser pick the right resolution
          // using the same source-selection algorithm as a real <img srcset>.
          link.setAttribute('imagesrcset', srcset);
          const sizes = img.getAttribute('sizes');
          if (sizes) link.setAttribute('imagesizes', sizes);
        }
        // href is required for browsers that don't support imagesrcset, and
        // for browsers that use it as the fallback URL.
        if (src && !src.startsWith('data:')) link.href = src;
        document.head.appendChild(link);
      });
    } catch (_) { /* parsing failed — silently skip preload */ }
  }

  function prefetchSectionData(url, wrapper) {
    const targets = getSectionTargets(wrapper);
    const sectionIds = [targets.main.id, targets.sticky.id].filter((value, index, all) => value && all.indexOf(value) === index);
    if (!sectionIds.length) return null;

    const requestUrl = buildSectionRequestUrl(url, sectionIds);
    const swapState = getCurrentAddonSwapState();
    const preparedKey = getPreparedSectionCacheKey(requestUrl, getAddonPreparationSignature(swapState));
    const preparedSectionMap = preparedKey ? getPreparedCached(preparedKey) : null;
    if (preparedSectionMap) {
      // Section HTML was already processed; trigger image preload in case this
      // is the first time this particular URL was hovered / queued.
      if (targets.main.id && preparedSectionMap[targets.main.id]) {
        preloadSectionGalleryImages(preparedSectionMap[targets.main.id]);
      }
      return Promise.resolve(preparedSectionMap);
    }

    return fetchSectionMap(requestUrl)
      .then((sectionMap) => buildPreparedAddonSectionMap(sectionMap, wrapper, requestUrl, swapState) || sectionMap)
      .then((sectionMap) => {
        // Kick off image preloads as soon as the section HTML arrives so
        // the images are in browser cache before the user clicks the swatch.
        if (sectionMap && targets.main.id && sectionMap[targets.main.id]) {
          preloadSectionGalleryImages(sectionMap[targets.main.id]);
        }
        return sectionMap;
      })
      .catch(() => null);
  }

  function prefetchTarget(url, wrapper) {
    if (!url || url === '#') return;
    const resolvedUrl = buildTargetProductUrl(url);
    speculate(resolvedUrl);
    prefetchSectionData(resolvedUrl, wrapper);
  }

  function eagerPrefetch(wrapper) {
    const seen = new Set();

    wrapper.querySelectorAll('a[data-value]').forEach((link, index) => {
      if (index >= EAGER_PREFETCH_LIMIT) return;

      const href = link.getAttribute('href');
      if (!href || href === '#') return;

      const resolvedUrl = buildTargetProductUrl(href);
      if (seen.has(resolvedUrl)) return;

      seen.add(resolvedUrl);
      speculate(resolvedUrl);
      prefetchSectionData(resolvedUrl, wrapper);
    });
  }

  /* ── Hover: label updates + speculation rules ── */
  function bindHoverHandlers(wrapper) {
    wrapper.querySelectorAll('.product-form__product-group').forEach(group => {
      getStoredSelectedLabel(group);
    });

    wrapper.querySelectorAll('.swatch-color, .bl-option-btn').forEach(swatch => {
      const group = swatch.closest('.product-form__product-group');
      const label = group ? group.querySelector('[data-selected-value]') : null;
      const original = label ? getStoredSelectedLabel(group, label.textContent) : '';

      swatch.addEventListener('pointerenter', () => {
        if (label) {
          label.textContent = swatch.getAttribute('data-color-name') || swatch.getAttribute('data-label') || '';
        }
        prefetchTarget(swatch.getAttribute('href'), wrapper);
      });

      swatch.addEventListener('mouseleave', () => {
        if (label) label.textContent = getStoredSelectedLabel(group, original);
      });

      swatch.addEventListener('touchstart', () => {
        prefetchTarget(swatch.getAttribute('href'), wrapper);
      }, { passive: true });
    });
  }

  /* ── Speculation rules / prefetch on hover ── */
  function speculate(href) {
    if (!href || href === '#') return;
    if (typeof HTMLScriptElement !== 'undefined' && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
      if (!document.querySelector('script[type="speculationrules"][data-href="' + CSS.escape(href) + '"]')) {
        const s = document.createElement('script');
        s.type = 'speculationrules';
        s.setAttribute('data-href', href);
        s.textContent = JSON.stringify({ prerender: [{ source: 'list', urls: [href] }] });
        document.head.appendChild(s);
      }
    } else if (!document.querySelector('link[rel="prefetch"][href="' + CSS.escape(href) + '"]')) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.appendChild(link);
    }
  }

  /* ── Click handler (event delegation) ── */
  function bindClickHandler(wrapper) {
    wrapper.addEventListener('click', (e) => {
      const btn = e.target.closest('a[data-value]');
      if (!btn) return;

      const href = btn.getAttribute('href');
      if (!href || href === '#') return;

      const resolvedUrl = buildTargetProductUrl(href);
      const targetUrl = new URL(resolvedUrl, window.location.href);
      const currentUrl = new URL(window.location.href);
      targetUrl.hash = '';
      currentUrl.hash = '';

      if (targetUrl.toString() === currentUrl.toString()) return;

      e.preventDefault();

      if (shouldBypassAjaxNavigation()) {
        window.location.href = appendWashbasinSelection(resolvedUrl);
        return;
      }

      applyOptimisticUpdate(btn, resolvedUrl);
      navigateAjax(resolvedUrl, wrapper, { historyAlreadyUpdated: true });
    });
  }

  function normalizeInitialHandleMatch(wrapper) {
    const target = wrapper?.getAttribute('data-bl-initial-handle-match-url');
    if (!target || target === '#') return false;

    const resolvedUrl = buildTargetProductUrl(target);
    const targetUrl = new URL(resolvedUrl, window.location.href);
    const currentUrl = new URL(window.location.href);
    targetUrl.hash = '';
    currentUrl.hash = '';

    const currentKey = currentUrl.toString();
    if (targetUrl.toString() === currentKey || initialHandleNormalizationKeys.has(currentKey)) return false;

    initialHandleNormalizationKeys.add(currentKey);
    history.replaceState({}, '', targetUrl.toString());
    navigateAjax(targetUrl.toString(), wrapper, { historyAlreadyUpdated: true });
    return true;
  }

  /* ── Pre-swap cleanup: preserve addon state, destroy only what won't survive ── */
  function destroyAddonInstances() {
    captureCurrentAddonSwapState();
    capturePersistentAddonBlockState();
    capturePersistentWashbasinBlockState();
    capturePersistentMediaGalleryState();
    capturePersistentTolstoyBlockState();

    // Only destroy washbasin instances that are NOT being preserved
    document.querySelectorAll('[data-washbasin-selector-v2][data-initialized]').forEach(el => {
      if (persistentWashbasinBlockState?.container === el) return;
      if (el.__blWbInstance && typeof el.__blWbInstance.destroy === 'function') {
        el.__blWbInstance.destroy();
      }
    });
    // BLProductCustomizer: call destroy if available
    document.querySelectorAll('bl-product-customizer').forEach(el => {
      if (persistentAddonBlockState?.customizer === el) return;
      if (typeof el.destroy === 'function') el.destroy();
    });
    // Preserve washbasin price state through AJAX swaps to avoid a visible
    // flash of the native price before the new selector instance rebuilds it.
    document.body.classList.remove('wb-btn-ready');
  }

  function prepareIncomingMediaGallery(newNode, preSwapGalleryHeight, preSwapMediaGalleryHeight) {
    const mediaGallery = newNode?.querySelector('media-gallery[data-bl-server-gallery-filter="true"]');
    if (!mediaGallery) return;

    // For desktop grid layouts (enableDesktopSlider=false), Swiper never initialises,
    // so the revealWhenStable loop in postProcessSectionSwap would hold opacity:0 for
    // ~400ms before timing out.  The gallery content is ordered synchronously before
    // the first paint, so no wrong-order flash occurs without pre-masking.
    const isDesktopGrid = mediaGallery.dataset.enableDesktopSlider !== 'true'
      && !window.FoxTheme?.config?.mqlMobile;
    if (isDesktopGrid) return;

    mediaGallery.style.opacity = '0';
    mediaGallery.style.pointerEvents = 'none';
    if (preSwapMediaGalleryHeight > 0) {
      mediaGallery.style.minHeight = preSwapMediaGalleryHeight + 'px';
    }

    const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (viewer && preSwapGalleryHeight > 0) {
      viewer.style.minHeight = preSwapGalleryHeight + 'px';
    }

    const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbnails) {
      thumbnails.style.opacity = '0';
      thumbnails.style.pointerEvents = 'none';
    }
  }

  function postProcessSectionSwap(newNode, url, mainSectionId, preSwapGalleryHeight, preSwapMediaGalleryHeight) {
    restorePersistentAddonBlock(newNode);
    restorePersistentWashbasinBlock(newNode);
    restorePersistentTolstoyBlock(newNode);

    const variant = getSelectedVariantFromSection(newNode);
    const variantHasFeaturedMedia = Boolean(variant?.featured_media?.id || variant?.featured_image?.id);
    const hasServerFilteredGallery = Boolean(newNode.querySelector('media-gallery[data-bl-server-gallery-filter="true"]'));
    const reusedPersistentMediaGallery =
      !hasServerFilteredGallery
      && variantHasFeaturedMedia
      && restorePersistentMediaGallery(newNode);

    const mediaGallery = newNode.querySelector('media-gallery');
    if (mediaGallery && reusedPersistentMediaGallery && variant && typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(variant);
      mediaGallery.sliderInstance?.slider?.update?.();
      mediaGallery.sliderInstance?.slider?.updateAutoHeight?.(0);
    } else if (mediaGallery && typeof mediaGallery.init === 'function') {
      // ── Compute the correct starting slide from the incoming variant so we
      //    can pass it to Swiper as `initialSlide` rather than letting Swiper
      //    render slide 0 and then immediately calling slideTo(N).  The latter
      //    produces a 1-frame flash on the first swap (Swiper's autoHeight
      //    callback can fire asynchronously even when speed = 0).
      const _initialSlide = (() => {
        if (!variant) return 0;
        const fm = variant.featured_media ||
          (variant.featured_image
            ? { position: variant.featured_image.position || 1 }
            : null);
        return fm ? Math.max(0, (fm.position || 1) - 1) : 0;
      })();

      // Temporarily override setSliderOptions so that, the moment it rebuilds
      // sliderOptions inside init(), we can inject `initialSlide` before
      // FoxTheme.Carousel/Swiper is constructed.  The override deletes itself
      // after the first call, restoring the prototype method.
      if (!hasServerFilteredGallery && _initialSlide > 0) {
        mediaGallery.setSliderOptions = function _patchedSetSliderOptions() {
          delete this.setSliderOptions; // restore prototype method immediately
          this.setSliderOptions();      // call original (now via prototype)
          this.sliderOptions.initialSlide = _initialSlide;
        };
      }

      // ── Apply a min-height on the incoming GalleryViewer equal to the
      //    outgoing viewer height.  This prevents the layout from collapsing
      //    to 0 while Swiper's autoHeight reads 0 for unloaded images.
      //
      //    IMPORTANT: clearMinHeight must call updateAutoHeight(0) BEFORE
      //    removing min-height.  If min-height is removed first, there is a
      //    window where Swiper still has height:0 and min-height is gone →
      //    the viewer collapses → that collapse is the visible "flash".
      //    Calling updateAutoHeight(0) forces Swiper to re-measure the now-
      //    loaded image synchronously, setting the correct height, so the
      //    min-height removal is a no-op from a layout perspective.
      const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
      if (viewer && preSwapGalleryHeight > 0) {
        viewer.style.minHeight = preSwapGalleryHeight + 'px';

        const clearMinHeight = () => {
          // Force Swiper to adopt the loaded image height before we lift the
          // min-height guard; without this the viewer momentarily collapses.
          const sl = mediaGallery.sliderInstance?.slider;
          if (sl && typeof sl.updateAutoHeight === 'function') {
            sl.updateAutoHeight(0);
          }
          viewer.style.minHeight = '';
        };

        const firstImg = viewer.querySelector('img');
        if (firstImg?.complete && firstImg.naturalHeight > 0) {
          clearMinHeight();
        } else if (firstImg) {
          firstImg.addEventListener('load', clearMinHeight, { once: true });
          firstImg.addEventListener('error', clearMinHeight, { once: true });
          // Safety net: clear regardless after 1.5 s so nothing stays locked
          setTimeout(clearMinHeight, 1500);
        } else {
          requestAnimationFrame(clearMinHeight);
        }
      }

      const releaseGalleryMask = () => {
        mediaGallery.style.opacity = '';
        mediaGallery.style.pointerEvents = '';
        mediaGallery.style.minHeight = '';
        thumbnails?.style.removeProperty('opacity');
        thumbnails?.style.removeProperty('pointer-events');
      };

      const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');

      // Desktop grid layouts have no Swiper — skip the opacity masking and
      // revealWhenStable machinery that wait for swiper-initialized (which never fires).
      const isDesktopGrid = hasServerFilteredGallery
        && mediaGallery.dataset.enableDesktopSlider !== 'true'
        && !window.FoxTheme?.config?.mqlMobile;

      if (hasServerFilteredGallery && !isDesktopGrid) {
        mediaGallery.style.opacity = '0';
        mediaGallery.style.pointerEvents = 'none';
        if (preSwapMediaGalleryHeight > 0) {
          mediaGallery.style.minHeight = preSwapMediaGalleryHeight + 'px';
        }
        if (thumbnails) {
          thumbnails.style.opacity = '0';
          thumbnails.style.pointerEvents = 'none';
        }
      }

      if (typeof mediaGallery.destroySlider === 'function') mediaGallery.destroySlider();
      mediaGallery.sliderInstance = false;
      mediaGallery.thumbsInstance = false;
      mediaGallery.init();

      if (hasServerFilteredGallery) {
        if (isDesktopGrid) {
          // Grid layout: no Swiper, no masking — clear any opacity set by
          // prepareIncomingMediaGallery on browsers that ran it before the guard.
          releaseGalleryMask();
        } else {
          const firstImg = viewer?.querySelector('img');
          let rafAttempts = 0;
          const maxRafAttempts = 24;

          const isImageReady = () => {
            return !firstImg || (firstImg.complete && firstImg.naturalHeight > 0);
          };

          const isGalleryReady = () => {
            // For grid layouts (sliderInstance remains false after init),
            // swiper-initialized never fires — treat as ready once image loads.
            const swiperActive = mediaGallery.sliderInstance !== false;
            const viewerReady = !viewer || !swiperActive || viewer.classList.contains('swiper-initialized');
            const thumbsReady = !thumbnails || !swiperActive || thumbnails.classList.contains('swiper-initialized');
            return viewerReady && thumbsReady && isImageReady();
          };

          const revealWhenStable = () => {
            rafAttempts += 1;
            if (isGalleryReady() || rafAttempts >= maxRafAttempts) {
              if (viewer?.style.minHeight) {
                const sl = mediaGallery.sliderInstance?.slider;
                if (sl && typeof sl.updateAutoHeight === 'function') {
                  sl.updateAutoHeight(0);
                }
                viewer.style.minHeight = '';
              }
              releaseGalleryMask();
              return;
            }
            requestAnimationFrame(revealWhenStable);
          };

          requestAnimationFrame(revealWhenStable);
          setTimeout(() => {
            if (viewer?.style.minHeight) viewer.style.minHeight = '';
            releaseGalleryMask();
          }, 1200);
        }
      }
    }

    if (mediaGallery && !hasServerFilteredGallery && variant && typeof mediaGallery.setActiveMedia === 'function') {
      // setActiveMedia is still called as a safety net (e.g. when featured_media
      // has no position, or for the desktop-stack / non-slider layout path that
      // uses sortMediaItems instead of slideTo).  When initialSlide was already
      // set correctly above, slideTo() is a fast no-op.
      mediaGallery.setActiveMedia(variant);
    }

    window?.Shopify?.PaymentButton?.init();
    window?.ProductModel?.loadShopifyXR();

    if (window.blVariantSeoData) {
      const newVariantInput = newNode.querySelector('input[name="id"]');
      if (newVariantInput && window.blVariantSeoData[newVariantInput.value]) {
        document.querySelectorAll('.bl-variant-seo-text').forEach(block => {
          block.innerHTML = window.blVariantSeoData[newVariantInput.value];
        });
      }
    }

    if (variant) {
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant }
      }));
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variant }
      }));

      window.FoxTheme?.pubsub?.publish?.(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: mainSectionId,
          html: newNode,
          variant
        }
      });
    }

    document.dispatchEvent(new CustomEvent('bl:grouping:swapped', {
      detail: {
        url,
        sectionId: mainSectionId,
        variant
      }
    }));
  }

  /* ── AJAX navigation: fetch + swap section only ── */
  function navigateAjax(url, wrapper, options = {}) {
    const targets = getSectionTargets(wrapper);
    const sectionIds = [targets.main.id, targets.sticky.id].filter((value, index, all) => value && all.indexOf(value) === index);
    const viewportPosition = captureViewportPosition();

    if (!targets.main.node || !targets.main.id || !sectionIds.length) {
      window.location.href = url;
      return;
    }

    // Abort any in-flight request
    if (abortController) abortController.abort();
    abortController = new AbortController();
    const navigationSignal = abortController.signal;

    // Destroy addon instances before swap to prevent stale listeners/state.
    destroyAddonInstances();

    const requestUrl = buildSectionRequestUrl(url, sectionIds);
    const addonPreparedCacheKey = getPreparedSectionCacheKey(requestUrl, getAddonPreparationSignature(window.__blAddonConfiguratorSwapState));
    const preparedSectionMap = addonPreparedCacheKey ? getPreparedCached(addonPreparedCacheKey) : null;
    const cachedSectionMap = preparedSectionMap || getCached(requestUrl);
    const hasPendingPrefetch = pendingPrefetches.has(requestUrl);
    const shouldShowLoadingState = !cachedSectionMap && !hasPendingPrefetch;

    if (shouldShowLoadingState) {
      applyLoadingState(targets.main.node, true);
      if (targets.sticky.node && targets.sticky.node !== targets.main.node) {
        applyLoadingState(targets.sticky.node, true);
      }
    }

    const sectionRequest = cachedSectionMap
      ? Promise.resolve(cachedSectionMap)
      : fetchSectionMap(requestUrl, { signal: navigationSignal })
        .then((sectionMap) =>
          buildPreparedAddonSectionMap(sectionMap, wrapper, requestUrl, window.__blAddonConfiguratorSwapState) || sectionMap
        );

    sectionRequest
      .then(sectionMap => {
        if (navigationSignal.aborted) return;

        const nextMainSection = parseSectionMarkup(sectionMap[targets.main.id], targets.main.node);
        if (!nextMainSection) throw new Error(`Missing section HTML for ${targets.main.id}`);

        const incomingBundleState =
          buildIncomingBundleState(nextMainSection)
          || buildIncomingAddonBundleState(nextMainSection);
        if (incomingBundleState?.formattedTotal) {
          // Inject into the NEW section DOM so the price is visible immediately
          // after the swap — no gap before washbasin re-initializes.
          injectBundlePriceIntoNode(nextMainSection, targets.main.id, incomingBundleState);
          window.BLBundlePriceState?.setOwnerState?.(
            incomingBundleState.owner || 'washbasin-v2',
            true,
            { buttonReady: true }
          );
          window.BLAtcPriceSync?.applyBundlePrice(incomingBundleState.owner || 'washbasin-v2', incomingBundleState.formattedTotal);
        } else {
          // No basin addon — clear override so native price is visible immediately.
          window.BLBundlePriceState?.setOwnerState?.('washbasin-v2', false);
          window.BLBundlePriceState?.setOwnerState?.('addon-configurator', false);
          window.BLAtcPriceSync?.clearBundlePrice('washbasin-v2');
          window.BLAtcPriceSync?.clearBundlePrice('addon-configurator');
        }

        updateDocumentTitle(nextMainSection);

        // Capture the current gallery viewer height BEFORE the swap so we can
        // hold the new gallery at the same height until its first image loads.
        // This prevents the Swiper autoHeight flash caused by unloaded images
        // momentarily reporting 0 height on first-swap (images are browser-cached
        // after the first swap, so subsequent swaps never flash).
        const _preSwapGalleryHeight = (() => {
          const viewer = targets.main.node.querySelector('media-gallery [id^="GalleryViewer"]');
          return viewer ? viewer.offsetHeight : 0;
        })();
        const _preSwapMediaGalleryHeight = (() => {
          const gallery = targets.main.node.querySelector('media-gallery');
          return gallery ? gallery.offsetHeight : 0;
        })();

        HTMLUpdateUtility.viewTransition(
          targets.main.node,
          nextMainSection,
          [
            (incomingNode) => prepareIncomingMediaGallery(
              incomingNode,
              _preSwapGalleryHeight,
              _preSwapMediaGalleryHeight
            )
          ],
          [
            (newNode) => postProcessSectionSwap(
              newNode,
              url,
              targets.main.id,
              _preSwapGalleryHeight,
              _preSwapMediaGalleryHeight
            )
          ]
        );

        if (
          targets.sticky.node
          && targets.sticky.id
          && targets.sticky.node !== targets.main.node
          && sectionMap[targets.sticky.id]
        ) {
          const nextStickySection = parseSectionMarkup(sectionMap[targets.sticky.id], targets.sticky.node);
          if (nextStickySection) {
            if (incomingBundleState?.formattedTotal) {
              injectBundlePriceIntoNode(nextStickySection, targets.sticky.id, incomingBundleState);
            }
            HTMLUpdateUtility.viewTransition(targets.sticky.node, nextStickySection);
          }
        }

        if (!options.historyAlreadyUpdated) {
          history.pushState({}, '', new URL(url, window.location.href).toString());
        }

        init();
        restoreViewportPosition(viewportPosition);
      })
      .catch(err => {
        if (shouldShowLoadingState) {
          applyLoadingState(targets.main.node, false);
          if (targets.sticky.node && targets.sticky.node !== targets.main.node) {
            applyLoadingState(targets.sticky.node, false);
          }
        }

        if (navigationSignal.aborted || err.name === 'AbortError') return;
        window.location.href = url;
      });
  }

  /* ── Popstate: back/forward button support ── */
  function bindPopstate() {
    if (window.__blGroupingPopstateBound) return;
    window.__blGroupingPopstateBound = true;

    window.addEventListener('popstate', () => {
      navigateAjax(window.location.href, document.querySelector('[data-bl-grouping-root]'), { historyAlreadyUpdated: true });
    });
  }

  // Run on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
